import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { LanguageService } from '../../core/services/language.service';
import { SubscriptionsService } from '../../core/services/subscriptions.service';
import { TranslatePipe } from '../../core/pipes/translate.pipe';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  private static readonly DISMISS_KEY = 'iwan_announcement_dismissed_at';
  private static readonly DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  announcementDismissed = false;
  mobileMenuOpen = false;
  /** Publish link path */
  publishLink: string = '/writer-plans';
  /** Query params for publish link when writer is subscribed */
  publishLinkQueryParams: { tab: string } | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    public auth: AuthService,
    public language: LanguageService,
    private router: Router,
    private subscriptions: SubscriptionsService
  ) {}

  private updatePublishLink(): void {
    if (!this.auth.isAuthenticated) {
      this.publishLink = '/publish-book';
      this.publishLinkQueryParams = null;
      return;
    }
    this.subscriptions.hasActiveWriterSubscription()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: hasActive => {
          if (hasActive) {
            this.publishLink = '/profile';
            this.publishLinkQueryParams = { tab: 'publish-book' };
          } else {
            this.publishLink = '/writer-plans';
            this.publishLinkQueryParams = null;
          }
        }
      });
  }

  ngOnInit(): void {
    this.announcementDismissed = this.isAnnouncementRecentlyDismissed();
    this.updatePublishLink();
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.updatePublishLink();
        this.mobileMenuOpen = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  onSearch(q: string): void {
    this.router.navigate(['/books'], { queryParams: q?.trim() ? { q: q.trim() } : {} });
  }

  onToggleLang(): void {
    this.language.toggleLang();
  }

  dismissAnnouncement(): void {
    this.announcementDismissed = true;
    try {
      localStorage.setItem(HeaderComponent.DISMISS_KEY, Date.now().toString());
    } catch { /* storage unavailable */ }
  }

  private isAnnouncementRecentlyDismissed(): boolean {
    try {
      const dismissed = localStorage.getItem(HeaderComponent.DISMISS_KEY);
      if (!dismissed) return false;
      return Date.now() - Number(dismissed) < HeaderComponent.DISMISS_DURATION_MS;
    } catch {
      return false;
    }
  }
}
